import React, { useState, useEffect } from 'react';
import { TipSummary } from '../../layout/FundedTips/TipSummary';
import PropTypes from 'prop-types';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import Collapse from '@material-ui/core/Collapse';
import Divider from '@material-ui/core/Divider';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import PhoneIcon from '@material-ui/icons/Phone';
import HomeIcon from '@material-ui/icons/Home';
import { Skeleton } from '@material-ui/lab';

import { useStyles } from './LookupPage.styles';
import { useCustomerDetail } from '../../../hooks/useCustomerLookup/useCustomerLookup';
import { formatOrderEvent } from '../../../utils/formatOrderEvent';

const formatCurrency = (val) =>
  val != null ? `$${Number(val).toFixed(2)}` : '—';

/**
 * Formats a timestamp from the API.
 * The API returns datetime strings (e.g. "2024-04-01 11:00:00"); numeric epoch
 * seconds are also handled for resilience. The space separator is normalised to
 * "T" so all browsers parse it correctly.
 */
const formatTimestamp = (ts) => {
  if (ts == null || ts === '') return '—';
  const d =
    typeof ts === 'number'
      ? new Date(ts * 1000)
      : new Date(String(ts).replace(' ', 'T'));
  return isNaN(d.getTime()) ? String(ts) : d.toLocaleString();
};

function SectionHeader({ label }) {
  const classes = useStyles();
  return (
    <Typography className={classes.panelSectionHeader}>{label}</Typography>
  );
}

SectionHeader.propTypes = {
  label: PropTypes.string.isRequired,
};

function PanelSkeleton({ onClose }) {
  const classes = useStyles();
  return (
    <Box className={classes.panelContainer}>
      <Box className={classes.panelHeader}>
        <Box flex={1}>
          <Skeleton variant="text" width="50%" height={22} />
          <Skeleton variant="text" width="70%" height={16} style={{ marginTop: 4 }} />
        </Box>
        <IconButton size="small" onClick={onClose} aria-label="close customer panel">
          <CloseIcon />
        </IconButton>
      </Box>
      <Box className={classes.panelBody}>
        {Array.from({ length: 6 }).map((_, i) => (
          <Box key={i} className={classes.skeletonWrapper}>
            <Skeleton variant="text" width="45%" height={20} />
            <Skeleton variant="text" width="75%" height={16} style={{ marginTop: 4 }} />
          </Box>
        ))}
      </Box>
    </Box>
  );
}

PanelSkeleton.propTypes = {
  onClose: PropTypes.func.isRequired,
};

export function CustomerDetailPanel({ customerId, onClose, onSelectOrder }) {
  const classes = useStyles();
  const [orderPage, setOrderPage] = useState(1);
  const [errorsOpen, setErrorsOpen] = useState(false);

  useEffect(() => {
    setOrderPage(1);
  }, [customerId]);

  const { data, isLoading, error, refetch } = useCustomerDetail(customerId, orderPage);

  if (isLoading) {
    return <PanelSkeleton onClose={onClose} />;
  }

  if (error) {
    return (
      <Box className={classes.panelContainer}>
        <Box className={classes.panelHeader}>
          <Box flex={1}>
            <Typography className={classes.panelHeaderTitle}>Customer Details</Typography>
          </Box>
          <IconButton size="small" onClick={onClose} aria-label="close customer panel">
            <CloseIcon />
          </IconButton>
        </Box>
        <Box className={classes.errorContainer}>
          <Typography variant="body2" color="error">
            Failed to load customer details.
          </Typography>
          <Box display="flex" style={{ gap: 8 }}>
            <Button size="small" variant="contained" color="primary" onClick={() => refetch()}>
              Retry
            </Button>
            <Button size="small" variant="outlined" onClick={onClose}>
              Close
            </Button>
          </Box>
        </Box>
      </Box>
    );
  }

  if (!data) {
    return (
      <Box className={classes.panelContainer}>
        <Box className={classes.panelHeader}>
          <Box flex={1}>
            <Typography className={classes.panelHeaderTitle}>Customer Details</Typography>
          </Box>
          <IconButton size="small" onClick={onClose} aria-label="close customer panel">
            <CloseIcon />
          </IconButton>
        </Box>
        <Box className={classes.emptyStateContainer}>
          <Typography variant="body2" color="textSecondary">
            No customer data available.
          </Typography>
        </Box>
      </Box>
    );
  }

  const fullName =
    [data.firstName, data.lastName].filter(Boolean).join(' ') || '—';
  const orders = data.orders || {};
  const orderResults = orders.results || [];
  const pageSize = orders.pageSize || 20;
  const tags = data.tags || [];
  const priceLevels = data.priceLevels || [];
  const phones = data.phones || [];
  const addresses = data.addresses || [];
  const errors = data.errors || [];

  const hasTags = tags.length > 0 || priceLevels.length > 0;
  const hasOrderPrev = orderPage > 1;
  const hasOrderNext = orderResults.length >= pageSize;

  return (
    <Box className={classes.panelContainer}>
      <Box className={classes.panelHeader}>
        <Box flex={1}>
          <Typography className={classes.panelHeaderTitle}>{fullName}</Typography>
          <Typography variant="body2" color="textSecondary">
            {data.email || '—'}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} aria-label="close customer panel">
          <CloseIcon />
        </IconButton>
      </Box>

      <Box className={classes.panelBody}>
        <Box className={classes.panelSection}>
          <SectionHeader label="Profile" />
          <Box className={classes.panelInfoRow}>
            <Typography variant="body2" color="textSecondary">
              Lifetime orders
            </Typography>
            <Typography variant="body2">{orders.allTimeCount ?? '—'}</Typography>
          </Box>
          <Box className={classes.panelInfoRow}>
            <Typography variant="body2" color="textSecondary">
              Lifetime spend
            </Typography>
            <Typography variant="body2">
              {formatCurrency(orders.allTimeTotal)}
            </Typography>
          </Box>
        </Box>

        {hasTags && (
          <>
            <Divider />
            <Box className={classes.panelSection}>
              {tags.length > 0 && (
                <Box mb={priceLevels.length > 0 ? 1.5 : 0}>
                  <SectionHeader label="Tags" />
                  <Box className={classes.chipRow}>
                    {tags.map((tag) => (
                      <Chip key={tag.id} label={tag.name} size="small" />
                    ))}
                  </Box>
                </Box>
              )}
              {priceLevels.length > 0 && (
                <Box>
                  <SectionHeader label="Price Levels" />
                  <Box className={classes.chipRow}>
                    {priceLevels.map((pl, i) => (
                      <Chip
                        key={i}
                        label={`Tier ${pl.tierLevel}`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </>
        )}

        <Divider />

        <Box className={classes.panelSection}>
          <SectionHeader label="Contact" />
          {phones.length > 0 ? (
            phones.map((phone) => (
              <Box key={phone.id} className={classes.contactRow}>
                <PhoneIcon fontSize="small" className={classes.contactIcon} />
                <Typography variant="body2">{phone.number}</Typography>
              </Box>
            ))
          ) : (
            <Typography variant="body2" color="textSecondary">
              No phone numbers on file
            </Typography>
          )}

          <Box mt={phones.length > 0 ? 1 : 0}>
            {addresses.length > 0 ? (
              addresses.map((addr) => (
                <Box key={addr.id} className={classes.contactRow}>
                  <HomeIcon fontSize="small" className={classes.contactIcon} />
                  <Box flex={1}>
                    <Typography variant="body2">
                      {[addr.address1, addr.city, addr.state, addr.zip]
                        .filter(Boolean)
                        .join(', ')}
                    </Typography>
                    {!addr.isActive && (
                      <Chip
                        label="Inactive"
                        size="small"
                        style={{
                          marginTop: 4,
                          height: 18,
                          fontSize: '0.7rem',
                        }}
                      />
                    )}
                  </Box>
                </Box>
              ))
            ) : (
              <Typography variant="body2" color="textSecondary">
                No addresses on file
              </Typography>
            )}
          </Box>
        </Box>

        <Divider />

        <Box className={classes.panelSection}>
          <SectionHeader label="Order History (last 12 months)" />
          {orderResults.length === 0 ? (
            <Typography variant="body2" color="textSecondary">
              No orders in the last 12 months.
            </Typography>
          ) : (
            <>
              {orderResults.map((order) => {
                const orderId = order.displayId || String(order.id);
                return (
                  <Box
                    key={order.id || order.displayId}
                    className={classes.orderHistoryRow}
                    onClick={() => onSelectOrder && onSelectOrder(orderId)}
                    role="button"
                    tabIndex={0}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && onSelectOrder)
                        onSelectOrder(orderId);
                    }}
                  >
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="flex-start"
                    >
                      <Typography
                        variant="body2"
                        style={{ fontWeight: 600 }}
                      >
                        {orderId}
                      </Typography>
                      <Typography variant="body2" style={{ fontWeight: 600 }}>
                        {formatCurrency(order.total)}
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="caption" color="textSecondary">
                        {order.restaurantName || '—'} &middot;{' '}
                        {order.status || '—'}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                         {formatOrderEvent(order.createdAtUtc, order.restaurantTimezone)}
                      </Typography>
                    </Box>
                    {order.cancelNote && (
                      <Typography
                        variant="caption"
                        style={{ color: '#d32f2f', display: 'block' }}
                      >
                        Cancel: {order.cancelNote}
                      </Typography>
                    )}
                    <TipSummary order={order} />
                  </Box>
                );
              })}

              {(hasOrderPrev || hasOrderNext) && (
                <Box className={classes.paginationBar} style={{ marginTop: 8 }}>
                  <Button
                    size="small"
                    disabled={!hasOrderPrev}
                    onClick={() => setOrderPage((p) => p - 1)}
                  >
                    Prev
                  </Button>
                  <Typography variant="caption" color="textSecondary">
                    Page {orderPage}
                  </Typography>
                  <Button
                    size="small"
                    disabled={!hasOrderNext}
                    onClick={() => setOrderPage((p) => p + 1)}
                  >
                    Next
                  </Button>
                </Box>
              )}
            </>
          )}
        </Box>

        <Divider />

        <Box className={classes.panelSection}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            className={classes.collapsibleHeader}
            onClick={() => setErrorsOpen((v) => !v)}
            role="button"
            tabIndex={0}
            aria-expanded={errorsOpen}
            onKeyPress={(e) => {
              if (e.key === 'Enter') setErrorsOpen((v) => !v);
            }}
          >
            <Typography className={classes.panelSectionHeader} style={{ marginBottom: 0 }}>
              Customer Errors{errors.length > 0 ? ` (${errors.length})` : ''}
            </Typography>
            {errorsOpen ? (
              <ExpandLessIcon fontSize="small" />
            ) : (
              <ExpandMoreIcon fontSize="small" />
            )}
          </Box>
          <Collapse in={errorsOpen}>
            <Box mt={1}>
              {errors.length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                  No errors on record.
                </Typography>
              ) : (
                errors.map((err) => (
                  <Box key={err.id} className={classes.errorRow}>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" style={{ fontWeight: 600 }}>
                        {err.errorType || '—'}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {formatTimestamp(err.created)}
                      </Typography>
                    </Box>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      style={{ display: 'block' }}
                    >
                      Solution: {err.solutionType || '—'} &middot; Cost:{' '}
                      {formatCurrency(err.cost)}
                    </Typography>
                    {err.note && (
                      <Typography
                        variant="caption"
                        style={{ display: 'block' }}
                      >
                        {err.note}
                      </Typography>
                    )}
                  </Box>
                ))
              )}
            </Box>
          </Collapse>
        </Box>
      </Box>
    </Box>
  );
}

CustomerDetailPanel.propTypes = {
  customerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  onClose: PropTypes.func.isRequired,
  onSelectOrder: PropTypes.func,
};
